import { useMemo } from 'react';
import { themePalette, type ColorTheme } from './themeSettings';
import { useThemeSettings } from './ThemeContext';

export type ChartPalette = {
  /** Main series — bars, primary lines */
  primary: string;
  /** Lighter primary — area strokes */
  primaryLight: string;
  /** Companion series (clearly different hue from primary) */
  secondary: string;
  secondaryLight: string;
  tertiary: string;
  positive: string;
  negative: string;
  warning: string;
  neutral: string;
  /** Multi-slice / multi-bar colors — high contrast, not all theme greens */
  series: string[];
  /** Dedicated pair for 2-slice pies (collected vs outstanding, etc.) */
  piePair: [string, string];
  /** Dedicated trio for common 3-bar charts (revenue / expense / profit) */
  barTrio: [string, string, string];
  grid: string;
  axis: string;
  gradientStart: string;
  areaGradientId: string;
};

/** Fixed high-contrast accents so categories stay readable next to the theme primary. */
const CONTRAST = {
  sky: '#0284c7',
  skyLight: '#38bdf8',
  amber: '#d97706',
  amberLight: '#fbbf24',
  violet: '#7c3aed',
  violetLight: '#a78bfa',
  rose: '#e11d48',
  roseLight: '#fb7185',
  slate: '#475569',
  teal: '#0f766e',
  indigo: '#4338ca',
  orange: '#ea580c',
};

export function buildChartPalette(theme: ColorTheme, dark = false): ChartPalette {
  const p = themePalette(theme);
  const primary = dark ? p.shades[400] : p.shades[700];
  const primaryLight = dark ? p.shades[300] : p.shades[500];
  const positive = dark ? p.shades[400] : p.shades[600];
  return {
    primary,
    primaryLight,
    // Secondary is intentionally a different family (sky/violet) so bars/pies don't blend.
    secondary: dark ? CONTRAST.skyLight : CONTRAST.sky,
    secondaryLight: CONTRAST.skyLight,
    tertiary: dark ? CONTRAST.violetLight : CONTRAST.violet,
    positive,
    negative: dark ? CONTRAST.roseLight : CONTRAST.rose,
    warning: dark ? CONTRAST.amberLight : CONTRAST.amber,
    neutral: dark ? '#a1a1aa' : CONTRAST.slate,
    series: dark
      ? [
          p.shades[400],
          CONTRAST.skyLight,
          CONTRAST.amberLight,
          CONTRAST.violetLight,
          CONTRAST.roseLight,
          '#818cf8',
          '#fb923c',
          '#2dd4bf',
          '#d4d4d8',
        ]
      : [
          p.shades[600],
          CONTRAST.sky,
          CONTRAST.amber,
          CONTRAST.violet,
          CONTRAST.rose,
          CONTRAST.indigo,
          CONTRAST.orange,
          CONTRAST.teal,
          CONTRAST.slate,
        ],
    piePair: dark ? [p.shades[400], CONTRAST.amberLight] : [p.shades[600], CONTRAST.amber],
    barTrio: dark ? [p.shades[400], CONTRAST.amberLight, CONTRAST.skyLight] : [p.shades[600], CONTRAST.amber, CONTRAST.sky],
    grid: dark ? '#3f3f46' : '#f1f5f9',
    axis: dark ? '#a1a1aa' : '#94a3b8',
    gradientStart: primaryLight,
    areaGradientId: `chartArea-${theme}${dark ? '-dark' : ''}`,
  };
}

export function useChartTheme(): ChartPalette {
  const { colorTheme, darkMode } = useThemeSettings();
  return useMemo(() => buildChartPalette(colorTheme, darkMode), [colorTheme, darkMode]);
}
